import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AssessmentRecommendationTrustedHtml } from '../assessment-recommendation-trusted-html';

import { assessmentRecommendationDetailsMessages as messages } from './assessment-recommendation-details-messages';

import type {
	FastshiftAssessmentRecommendationDetails,
	FastshiftRecommendationInsight,
	FastshiftRecommendationTitleAndContent,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

const styles = cssMap({
	dataCard: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
	recommendationCard: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
	},
	recommendationIcon: {
		display: 'inline-flex',
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
});

const isHttpsUrl = (value: string) => {
	try {
		return new URL(value).protocol === 'https:';
	} catch {
		return false;
	}
};

const RecommendationDataCard = ({
	items,
	comment,
}: {
	items: FastshiftRecommendationTitleAndContent[];
	comment?: string | null;
}) => (
	<Box xcss={styles.dataCard}>
		<Stack space="space.150">
			{items.map((item, itemIndex) => (
				<Stack key={`${item.title}-${itemIndex}`} space="space.025">
					<Text size="small" color="color.text.subtle">
						{item.title}
					</Text>
					<Text>{item.content}</Text>
				</Stack>
			))}
			{comment && (
				<Text color="color.text.subtlest" size="small">
					{comment}
				</Text>
			)}
		</Stack>
	</Box>
);

const RecommendationCard = ({
	insight,
	onResourceLinkClick,
}: {
	insight: FastshiftRecommendationInsight;
	onResourceLinkClick?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const resources = insight.resources.filter((resource) => isHttpsUrl(resource.link));

	return (
		<Box xcss={styles.recommendationCard}>
			<Stack space="space.150">
				<Inline space="space.100" alignBlock="center">
					<Box xcss={styles.recommendationIcon}>
						<LightbulbIcon label="" color={token('color.icon.discovery')} />
					</Box>
					<Heading size="xsmall" as="h4">
						{insight.title}
					</Heading>
				</Inline>
				<AssessmentRecommendationTrustedHtml html={insight.insight} />
				{resources.length > 0 && (
					<Stack space="space.050">
						<Heading size="xxsmall" as="h5">
							{formatMessage(messages.resourcesTitle)}
						</Heading>
						{resources.map((resource, resourceIndex) => (
							<Text size="small" key={resourceIndex}>
								<Link
									href={resource.link}
									target="_blank"
									rel="noopener noreferrer"
									onClick={onResourceLinkClick}
								>
									{resource.text}
								</Link>
							</Text>
						))}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};

const RecommendationGroup = ({
	title,
	recommendations,
	onResourceLinkClick,
}: {
	title: string;
	recommendations: FastshiftAssessmentRecommendationDetails['insights'];
	onResourceLinkClick?: () => void;
}) => (
	<Stack space="space.150">
		<Heading size="xsmall" as="h3">
			{title}
		</Heading>
		{recommendations.map((insight, insightIndex) => (
			<RecommendationCard
				key={`${insight.title}-${insightIndex}`}
				insight={insight}
				onResourceLinkClick={onResourceLinkClick}
			/>
		))}
	</Stack>
);

export const AssessmentRecommendationDetailsContent = ({
	details,
	onResourceLinkClick,
}: {
	details: FastshiftAssessmentRecommendationDetails;
	onResourceLinkClick?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const summaryItems = [
		...(details.summary.dataSection ?? []),
		details.summary.customerData,
		details.summary.limit,
	].filter((item): item is FastshiftRecommendationTitleAndContent => item !== null);
	const recommendations = [
		...details.insights.filter((insight) => insight.isTopRecommendation),
		...details.insights.filter((insight) => !insight.isTopRecommendation),
	];
	return (
		<Stack space="space.400">
			<Stack space="space.150">
				{(summaryItems.length > 0 || details.summary.comment) && (
					<RecommendationDataCard items={summaryItems} comment={details.summary.comment} />
				)}
				<AssessmentRecommendationTrustedHtml html={details.summary.summary} />
				{isHttpsUrl(details.summary.learnMoreLink) && (
					<Text size="small">
						<Link
							href={details.summary.learnMoreLink}
							target="_blank"
							rel="noopener noreferrer"
							onClick={onResourceLinkClick}
						>
							{formatMessage(messages.learnMore)}
						</Link>
					</Text>
				)}
			</Stack>

			{details.insights.length > 0 && (
				<RecommendationGroup
					title={formatMessage(messages.recommendationsTitle)}
					recommendations={recommendations}
					onResourceLinkClick={onResourceLinkClick}
				/>
			)}
		</Stack>
	);
};
